import React from 'react';
import { Tooltip, Box, CircularProgress } from '@mui/material';
import { usePermissions } from '../../context/PermissionContext';

// Permission alias mapping - centralized for consistency
const PERMISSION_ALIASES = {
    'create': 'create',
    'add': 'create',
    'edit': 'update',
    'update': 'update',
    'delete': 'delete',
    'remove': 'delete',
    'view': 'view',
    'read': 'read'
};

const LoadingSpinner = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
        <CircularProgress size={40} sx={{ color: '#FFBD65' }} />
    </Box>
);

/**
 * PermissionWrapper Component
 * 
 * @param {string} permission - The permission type to check (e.g., 'create', 'update', 'delete', 'view')
 * @param {ReactNode} children - The component to be protected
 * @param {string} mode - 'hide' (completely remove) or 'disable' (keep visible but disabled with tooltip)
 * @param {string} message - Custom message to show in tooltip when disabled
 */
const PermissionWrapper = ({ 
    permission, 
    children, 
    mode = 'hide', 
    message 
}) => {
    const { permissions, loading, accessDenied } = usePermissions();
    
    const targetPerm = PERMISSION_ALIASES[permission?.toLowerCase()] || permission;
    const hasPermission = permissions[targetPerm];

    if (!children) {
        return null;
    }

    if (!React.isValidElement(children)) {
        if (hasPermission) {
            return children;
        }
        return mode === 'hide' ? null : children;
    }

    // If global access is denied, return null to prevent data fetching
    if (accessDenied) {
        return null;
    }

    // While loading, show a spinner for the view permission to prevent "empty page" flash
    if (loading && targetPerm === 'view') {
        return <LoadingSpinner />;
    }

    // If permission is 'view' and it's false, we always hide regardless of mode
    // BUT only if not loading (to prevent hiding during initial load)
    if (targetPerm === 'view' && !hasPermission && !loading) {
        return null;
    }

    if (hasPermission) {
        return children;
    }

    // Handle 'hide' mode
    if (mode === 'hide') {
        return null;
    }

    // Handle 'disable' mode
    const defaultMessage = message || `You do not have permission to ${permission}`;
    
    const handleDisabledClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <Tooltip title={defaultMessage} arrow placement="top">
            <Box 
                component="span" 
                onClickCapture={handleDisabledClick}
                onMouseDownCapture={handleDisabledClick}
                onMouseUpCapture={handleDisabledClick}
                sx={{ 
                    cursor: 'not-allowed !important', 
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'auto !important',
                    '& *': {
                        pointerEvents: 'none !important',
                        cursor: 'not-allowed !important',
                    }
                }}
            >
                {React.cloneElement(children, { 
                    disabled: true, 
                    sx: { 
                        ...children.props.sx, 
                        opacity: 0.4,
                        filter: 'grayscale(1)',
                        transition: 'none !important'
                    }
                })}
            </Box>
        </Tooltip>
    );
};

export default PermissionWrapper;
